import { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Avatar,
  Divider,
  Grid,
} from "@mui/material";
import GoogleIcon from "@mui/icons-material/Google";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@context/AuthContext";
import { useNotification } from "@context/NotificationContext";
import { useAppSelector } from "@/store";
import { ConfirmDialog } from "@components/ConfirmDialog";
import { PageHeader } from "@components/PageHeader";
import { LoadingCenter } from "@components/LoadingCenter";

export default function Account() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { notify } = useNotification();
  const { user, isAuthenticated, loading, loginWithGoogle, deleteAccount } =
    useAuth();
  const stats = useAppSelector((s) => s.scrapers.stats);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const handleDelete = async () => {
    try {
      await deleteAccount();
      notify(t("account.accountDeleted"), "success");
      navigate("/");
    } catch {
      notify(t("common.error"), "error");
    }
    setConfirmDelete(false);
  };

  if (loading) {
    return <LoadingCenter minHeight="60vh" />;
  }

  if (!isAuthenticated) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
        }}
      >
        <Card sx={{ maxWidth: 420, textAlign: "center" }}>
          <CardContent sx={{ p: 5 }}>
            <Typography
              variant="h5"
              gutterBottom
              sx={{
                fontWeight: "bold",
              }}
            >
              {t("account.loginTitle")}
            </Typography>
            <Typography
              sx={{
                color: "text.secondary",
                mb: 4,
              }}
            >
              {t("account.loginDescription")}
            </Typography>
            <Button
              variant="contained"
              size="large"
              startIcon={<GoogleIcon />}
              onClick={loginWithGoogle}
              sx={{ px: 4 }}
            >
              {t("account.loginWithGoogle")}
            </Button>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader>{t("account.title")}</PageHeader>
      <Card sx={{ maxWidth: 600 }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 3, mb: 3 }}>
            <Avatar
              sx={{
                width: 72,
                height: 72,
                bgcolor: "primary.main",
                fontSize: 28,
              }}
            >
              {user?.email?.charAt(0)?.toUpperCase() || "U"}
            </Avatar>
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: "bold",
                }}
              >
                {user?.email}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                }}
              >
                {t("account.profile")}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={2}>
            <Grid size={{ xs: 6 }}>
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                }}
              >
                {t("account.email")}
              </Typography>
              <Typography>{user?.email}</Typography>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                }}
              >
                {t("account.maxScrapers")}
              </Typography>
              <Typography>{stats?.maxScrapers || 10}</Typography>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteForeverIcon />}
            onClick={() => setConfirmDelete(true)}
          >
            {t("account.deleteAccount")}
          </Button>
        </CardContent>
      </Card>
      <ConfirmDialog
        open={confirmDelete}
        title={t("account.deleteAccount")}
        message={t("account.confirmDelete")}
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete(false)}
      />
    </Box>
  );
}
